import { TLocation } from "../game/map/mapTypes";
import { TInventoryItem } from "./inventory/inventoryTypes";

export type TCharacter={
    Name: string;
    IsRobot: boolean;
    IsChaser: boolean;
    Class?: ECharacterClass; //Humans and chasers don't have a class?
    Occupation?: EOccupations;
    Gender: EGenders;
    Description: string;
    IsVisible: boolean; //applies to npcs like chasers or villains. When true they can be seen on the map.
    Money: number; //All characters can have money, although many will probably never use it for anything.

/*
Stats: stats is a type and shows the current baseline stats for this character, e.g. Strength, Speed, Weight, MaxPower - w/e we need
Components: eventually for robot characters we will have a set of components whose sum will give the character stats. Humans will just have fixed stats.
Current state: this will hold any variables e.g. current hp, current power level, money, inventory, weight carried - things that can vary without changing the actual character - Inventory probably needs a whole new type
*/

}

//This type will have all the player specific characteristics
export type TPlayer = TCharacter & {
    Location: TLocation

    //common attributes
    Stats: TCharacterAttributes

    //player specific attributes
    MaximumCosmeticHP: number //the highest total HP currently attainable. For PCs determined by the toughness characteristic. May be modified by better builds later
    CurrentCosmeticHP: number //how many cosmetic hp left - damage will decrease PC ability to interact with NPCs in Town - charmer can mitigate. 

    // inventory
    Inventory: TInventoryItem[]

    //travel characteristics - only applies to the player
    TravelTo?: TLocation
    TravelTimeLeft?: number;
}

//the specific things about chasers, their AI characteristics if differentiated, previous route etc
export type TChaser = TCharacter & {
    Location?: TLocation
}
//This type includes all characteristic that allow interaction between characters. All characters that engage in combat etc must have this
export type TCharacterAttributes={
    //there should be opposition in attributes, for players anyway, e.g. increasing strength increases weight or reduces speed. 
    //possibly some attributes impose a penalty on others, being strong reduces speed, being fast reduces strength so to be both fast and strong 
    //requires spending more points than just double boosting one. 
    //a build system will be put in place that defines the attributes based on the components in the build. 

    Strength: number //used for inflicting damage in melee and subdue attempts
    Toughness: number //gives more stamina for fighting, and cosmetic hitpoints
    Reflexes: number //used for attacks, melee and ranged
    Speed: number //used for flee and dodge attempts - calculate from strength and reflexes in the first instance?
    Weight: number //calculated from strength and toughness, higher weight increases horse stamina use 
}

export enum EGenders{Male, Female, None}

export enum EOccupations {
    Farmhand = "Farmhand",
    Rancher = "Rancher",
    Gambler = "Gambler",
    Barkeep = "Barkeep",
    Banker = "Banker",
    Storekeeper = "Storekeeper",
    Prospector = "Prospector",
    Barber = "Barber"
}

export enum ECharacterClass {
    Melee = "Melee",
    Shooter = "Shooter",
    Mastermind = "Mastermind",
    Charmer = "Charmer"
}

//All the default values for the player characters at the pregeneration stage.
const preGenBase: TCharacter = {IsChaser: false, IsRobot: true,  IsVisible: true, Money: 0, Name: "", Description: "", Gender:EGenders.None}

//the prgenerated characters - these don't have money or a location or attributes, these are added when the characxter chooses a player.
export const preGenCharacters: TCharacter[]=[
    {...preGenBase, Name: "Andy", Class: ECharacterClass.Melee, Gender: EGenders.Male, Occupation: EOccupations.Farmhand, 
        Description: `It's peaceful life for you tending the fields for your boss. Except when you raze hell in {town}, always ending in a fist fight, and sometimes the local jail.`},
    {...preGenBase, Name: "Eddie", Class: ECharacterClass.Melee, Gender: EGenders.Male, Occupation: EOccupations.Prospector, 
        Description: `You used to hit the bars in {town} after panning enough nuggets, usually resulting in a blackout for you and black eyes for others. Now you're trying to stay on the wagon and save your cash.`},
    {...preGenBase, Name: "Debbie", Class: ECharacterClass.Melee, Gender: EGenders.Female, Occupation: EOccupations.Storekeeper, 
        Description: `It can be a rough life in {town}. But everybody knows to keep their nose clean in your general store, or you'll break it for 'em. You aint mean, you just don't take no guff.`},
    {...preGenBase, Name: "Milly", Class: ECharacterClass.Melee, Gender: EGenders.Female, Occupation: EOccupations.Rancher, 
        Description: `Bandits, injuns, no-good-drifters. You've seen 'em all, and you'll put them in their place if they don't show you respect down on your ranch just outside {town}.`},

    {...preGenBase, Name: "Bobbi", Class: ECharacterClass.Shooter, Gender: EGenders.Female, Occupation: EOccupations.Gambler, 
        Description: `How come they always accuse you of cheatin'? It's not your fault you're just lucky at cards. You've had to show that you can out-draw the best of them, more than once, when the chips were down.`},
    {...preGenBase, Name: "Frannie", Class: ECharacterClass.Shooter, Gender: EGenders.Female, Occupation: EOccupations.Farmhand, 
        Description: `Some days you're just minding the cows. Other times you're seeing off rustlers, but being a crack shot is always a plus when it's hiring time. The ranchers round {town} are always looking for a good woman.`}, 
    {...preGenBase,Name: "Jerry", Class: ECharacterClass.Shooter, Gender: EGenders.Male, Occupation: EOccupations.Banker,
        Description: `Oh it's a quiet life just working in the bank in {town} they said. Like hell. Just last week some no good thieves tried to rob you. But you showed them even a mild mannered clerk can be cold as ice in a shoot-out.`},
    {...preGenBase, Name: "Nobby", Class: ECharacterClass.Shooter, Gender: EGenders.Male, Occupation: EOccupations.Storekeeper, 
        Description: `It sure is busy being the undertaker in {town}. You go about your work with complete professionalism. But you keep a six-shooter with you, always, just in case, and you're real good with it too.`},

    {...preGenBase, Name: "Charlie", Class: ECharacterClass.Mastermind, Gender: EGenders.Male, Occupation: EOccupations.Rancher, 
        Description: `You gotta stay one step ahead of the competition in the {town} small holdings. It's positively cut-throat, but you always know what's going on, what everybody's thinking, almost before they know it themselves.`},
    {...preGenBase, Name: "Garry", Class: ECharacterClass.Mastermind, Gender: EGenders.Male, Occupation: EOccupations.Barkeep, 
        Description: `{town} is a ripe fruit for the picking. Working behind the bar in the local establishment gives you endless opportunities to trade favours and make money. One day soon you'll own the whole place.`},
    {...preGenBase, Name: "Katy", Class: ECharacterClass.Mastermind, Gender: EGenders.Female, Occupation: EOccupations.Banker, 
        Description: `It's amazing how bad at numbers 'most everybody in {town} is. Good job you're the one in charge at the bank, keeping all the loans, deposits, contracts and mortgages straight, while making an honest profit, of course.`},
    {...preGenBase, Name: "Pammy", Class: ECharacterClass.Mastermind, Gender: EGenders.Female, Occupation: EOccupations.Barber, 
        Description: `You love to know secrets. And people tell their barber the darndest things. Not that you'd ever do anything with all that know-how, surely, but an ace in the hole never hurts.`},

    {...preGenBase, Name: "Izzy", Class: ECharacterClass.Charmer, Gender: EGenders.Female, Occupation: EOccupations.Barkeep, 
        Description: `You wear your heart on your sleeve, falling in and out of love every other week with handsome customers who pour out their hearts while you pour them drinks in the {town} saloon.`},
    {...preGenBase, Name: "Hilary", Class: ECharacterClass.Charmer, Gender: EGenders.Female, Occupation: EOccupations.Prospector, 
        Description: `You live a hard life alone in the mountains looking for gold, but you love to paint the town red when you strike it rich, buying drinks for all your friends in {town}, and hell, anyone else who's thirsty!`},
    {...preGenBase, Name: "Lemmy", Class: ECharacterClass.Charmer, Gender: EGenders.Male, Occupation: EOccupations.Barber, 
        Description: `'A shave for you sir? Two dollars, best price in {town}.' You hear everything in the barbering business, especially when folks just like to talk and talk to an easy going fella like yourself.`},
    {...preGenBase, Name: "Robbie", Class: ECharacterClass.Charmer, Gender: EGenders.Male, Occupation: EOccupations.Gambler, 
        Description: `You live you life from one hand to the next, at the card tables in {town}. Sometimes you win, sometimes you lose, but no matter how bad the beat, you can always talk your way out of trouble.`},
]

//All the default values for the chasers. They all start invisible
const chaserBase: TCharacter = {IsChaser: true, IsRobot: false,  IsVisible: false, Money: 0, Name: "", Description: "", Gender:EGenders.None}

export const chasers: TCharacter[]=[
    {...chaserBase, Name:"Dirty Dan", Gender: EGenders.Male, Description:"Tall, ginger, and bearded."},
    {...chaserBase, Name:"Wily Wayne", Gender: EGenders.Male, Description: "Middling height, bald, and inscrutable."},
    {...chaserBase, Name:"Sneaky Steve", Gender: EGenders.Male, Description: "Middling height, blonde, and bespectacled."},
    {...chaserBase, Name:"Rootin Rhi", Gender: EGenders.Female, Description: "Short, dirty blonde, and bespectacled."},
    {...chaserBase, Name:"Lucky Luke", Gender: EGenders.Male, Description: "Short, dark, and hairy."}
]